import React, { useState, useMemo } from 'react';
import { goldButton } from '../PageShell';

const STATUS_OPTIONS = ['paid', 'preparing', 'out_for_delivery', 'arriving_soon', 'delivered', 'cancelled'];
const STATUS_LABELS = {
  paid: 'Confirmado', preparing: 'Preparando', out_for_delivery: 'En ruta',
  arriving_soon: 'Llegando (~20min)', delivered: 'Entregado', cancelled: 'Cancelado',
};

const parseYMD = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const toYMD = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Monday as the start of the week
const getMonday = (dt) => {
  const d = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const fmtDay = (d) => {
  if (!d) return 'Sin fecha';
  const dt = parseYMD(d);
  const s = dt.toLocaleDateString('es-PR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const fmtRange = (monday) => {
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const opts = { day: 'numeric', month: 'long' };
  const a = monday.toLocaleDateString('es-PR', opts);
  const b = sunday.toLocaleDateString('es-PR', { ...opts, year: 'numeric' });
  return `Semana del ${a} al ${b}`;
};

const AdminOrders = ({
  orders,
  selected,
  toggleSelect,
  changeStatus,
  confirmReceipt,
  previewRoute,
  setTicket,
  route,
}) => {
  // Determine the initial week offset: show the week of the newest delivery date with orders
  const initialOffset = useMemo(() => {
    const dates = orders.map((o) => o.delivery_date).filter(Boolean).sort();
    if (dates.length === 0) return 0;
    const newest = getMonday(parseYMD(dates[dates.length - 1]));
    const todayMonday = getMonday(new Date());
    const diffDays = Math.round((newest - todayMonday) / (1000 * 60 * 60 * 24));
    return Math.round(diffDays / 7);
  }, [orders]);

  const [weekOffset, setWeekOffset] = useState(initialOffset);

  // Compute the currently viewed week boundaries
  const viewMonday = useMemo(() => {
    const m = getMonday(new Date());
    m.setDate(m.getDate() + weekOffset * 7);
    return m;
  }, [weekOffset]);

  const weekStartStr = toYMD(viewMonday);
  const weekEndDate = useMemo(() => {
    const e = new Date(viewMonday);
    e.setDate(e.getDate() + 6);
    return e;
  }, [viewMonday]);
  const weekEndStr = toYMD(weekEndDate);

  // Filter orders that fall inside the viewed week, then group by delivery date (day)
  const { dayKeys, groups, weekOrders } = useMemo(() => {
    const inWeek = orders.filter((o) => {
      const d = o.delivery_date;
      return d && d >= weekStartStr && d <= weekEndStr;
    });
    const g = {};
    inWeek.forEach((o) => {
      const k = o.delivery_date || 'sin';
      (g[k] = g[k] || []).push(o);
    });
    const keys = Object.keys(g).sort();
    return { dayKeys: keys, groups: g, weekOrders: inWeek };
  }, [orders, weekStartStr, weekEndStr]);

  const weekTotal = weekOrders.reduce((s, o) => s + o.total, 0);
  const weekIds = weekOrders.map((o) => o.id);

  if (orders.length === 0) {
    return (
      <p className="font-body mt-6" style={{ fontSize: '14px', color: '#C2C1B6' }}>
        No hay órdenes pagadas todavía.
      </p>
    );
  }

  return (
    <>
      {route && (
        <div className="mt-5 p-4" style={{ background: '#141414', border: '1px solid #C9A84C' }} data-testid="route-result">
          <p className="font-display" style={{ fontSize: '16px', color: '#C9A84C' }}>
            ✓ Ruta enviada al chofer · {route.stops.length} paradas (de la más cercana a la más lejana)
          </p>
          <a
            href={route.maps_url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-body"
            style={{ fontSize: '13px', color: '#E8C96A', textDecoration: 'underline' }}
          >
            Abrir en Google Maps
          </a>
          <ol className="mt-3 space-y-1" style={{ listStyle: 'decimal', paddingLeft: '20px' }}>
            {route.stops.map((s) => (
              <li key={s.seq} className="font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>
                <strong style={{ color: '#F5F5F0' }}>{s.count} pedido(s)</strong> para {s.name}
                {s.leg_km != null ? ` · ${s.leg_km} km` : ''}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Week navigation */}
      <div className="mt-8 flex items-center justify-between flex-wrap gap-3 pb-4" style={{ borderBottom: '1px solid rgba(201,168,76,0.3)' }} data-testid="week-nav">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setWeekOffset((v) => v - 1)}
            className="font-body"
            style={{ background: 'none', border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' }}
            data-testid="week-prev"
            aria-label="Semana anterior"
          >
            ← Anterior
          </button>
          <button
            onClick={() => setWeekOffset((v) => v + 1)}
            className="font-body"
            style={{ background: 'none', border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' }}
            data-testid="week-next"
            aria-label="Semana siguiente"
          >
            Siguiente →
          </button>
          {weekOffset !== 0 && (
            <button
              onClick={() => setWeekOffset(0)}
              className="font-body"
              style={{ background: 'none', border: 'none', color: '#9A7522', padding: '8px 4px', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}
              data-testid="week-today"
            >
              Ir a esta semana
            </button>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <span className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Entregas</span>
          <h2 className="font-display" style={{ fontSize: '22px', fontWeight: 700, color: '#F5F5F0' }} data-testid="week-label">{fmtRange(viewMonday)}</h2>
          <p className="font-body" style={{ fontSize: '12px', color: '#C2C1B6' }}>{weekOrders.length} pedido(s) · ${weekTotal.toFixed(2)}</p>
        </div>
      </div>

      {weekOrders.length > 0 && (
        <div className="mt-4">
          <button onClick={() => previewRoute(weekIds)} style={goldButton} data-testid="week-route">
            Vista previa: ruta de toda la semana
          </button>
        </div>
      )}

      {dayKeys.length === 0 && (
        <p className="font-body mt-8" style={{ fontSize: '14px', color: '#C2C1B6' }} data-testid="week-empty">
          No hay entregas para esta semana. Usa los botones para navegar a otra semana.
        </p>
      )}

      {dayKeys.map((dk) => {
        const list = groups[dk];
        const dayTotal = list.reduce((s, o) => s + o.total, 0);
        const dayIds = list.map((o) => o.id);
        return (
          <div key={dk} className="mt-8" data-testid={`day-group-${dk}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 pb-3" style={{ borderBottom: '1px solid rgba(201,168,76,0.18)' }}>
              <div>
                <span className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Entrega</span>
                <h3 className="font-display" style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F0' }}>{fmtDay(dk)}</h3>
                <p className="font-body" style={{ fontSize: '12px', color: '#C2C1B6' }}>{list.length} pedido(s) · ${dayTotal.toFixed(2)}</p>
              </div>
              <button onClick={() => previewRoute(dayIds)} style={goldButton} data-testid={`day-route-${dk}`}>
                Vista previa: ruta de este día
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {list.map((o, oi) => (
                <div key={o.id} className="p-4" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.18)' }} data-testid={`admin-order-${o.id}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={!!selected[o.id]}
                      onChange={() => toggleSelect(o.id)}
                      style={{ marginTop: '5px', accentColor: '#C9A84C' }}
                      data-testid={`select-${o.id}`}
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <div>
                          <p className="font-display" style={{ fontSize: '16px', fontWeight: 700 }}>
                            <span className="font-bebas" style={{ color: '#C9A84C', marginRight: '8px' }}>#{oi + 1}</span>
                            {o.customer_name} · <span style={{ color: '#C2C1B6', fontSize: '13px' }}>{o.customer_phone}</span>
                          </p>
                          <p className="font-body mt-1" style={{ fontSize: '11px', color: '#9A7522' }}>
                            Código: {o.id.slice(0, 8).toUpperCase()}
                          </p>
                          {o.confirmed_by_customer && (
                            <p className="font-body mt-1" style={{ fontSize: '12px', color: '#7FB77E', fontWeight: 600 }} data-testid={`confirmed-${o.id}`}>
                              ✓ Entrega confirmada por el cliente
                            </p>
                          )}
                          {o.confirmed_by_admin && !o.confirmed_by_customer && (
                            <p className="font-body mt-1" style={{ fontSize: '12px', color: '#7FB77E', fontWeight: 600 }} data-testid={`confirmed-admin-${o.id}`}>
                              ✓ Entrega confirmada por admin
                            </p>
                          )}
                          <p className="font-body mt-1" style={{ fontSize: '13px', color: '#C2C1B6' }}>{o.address}</p>
                          {o.notes && <p className="font-body mt-1" style={{ fontSize: '12px', color: '#9A7522' }}>Nota: {o.notes}</p>}
                        </div>
                        <span className="font-bebas" style={{ fontSize: '24px', color: '#C9A84C' }}>${o.total.toFixed(2)}</span>
                      </div>
                      <div className="mt-2 font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>
                        {o.items.map((it) => `${it.qty}× ${it.name}${it.modifiers && it.modifiers.length ? ' (' + it.modifiers.join(', ') + ')' : ''}`).join(', ')}
                      </div>
                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <select
                          value={o.status}
                          onChange={(e) => changeStatus(o.id, e.target.value)}
                          style={{ background: '#0A0A0A', color: '#F5F5F0', border: '1px solid rgba(201,168,76,0.3)', padding: '7px 10px', fontSize: '13px' }}
                          data-testid={`status-select-${o.id}`}
                        >
                          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                        </select>
                        <button
                          onClick={() => setTicket({ ...o, seq: oi + 1, week: fmtDay(dk) })}
                          className="font-body"
                          style={{ fontSize: '12px', color: '#C9A84C', background: 'none', border: '1px solid rgba(201,168,76,0.4)', padding: '6px 12px', cursor: 'pointer' }}
                          data-testid={`ticket-${o.id}`}
                        >
                          Ticket
                        </button>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${o.lat},${o.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-body"
                          style={{ fontSize: '12px', color: '#C9A84C' }}
                        >
                          Ver ubicación
                        </a>
                        {['out_for_delivery', 'arriving_soon'].includes(o.status) && !o.confirmed_by_customer && !o.confirmed_by_admin && (
                          <button
                            onClick={() => confirmReceipt(o.id)}
                            className="font-body"
                            style={{ fontSize: '12px', color: '#F5F5F0', background: '#7FB77E', border: 'none', padding: '6px 12px', cursor: 'pointer' }}
                            data-testid={`confirm-receipt-${o.id}`}
                          >
                            Confirmar recibo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default AdminOrders;
